// Platform admin dashboard (archetype: Dashboard, simplified — no widget
// customization/localStorage registry; that is explicitly out of P0 scope).
//
// Live data: overview counters, LLM spend for the selected period, worker
// health snapshots, and outbox status summary. Auto-refreshes every 30s.
import { useState, type ReactElement } from 'react';
import { Alert, Button, Card, Col, Empty, Row, Select, Skeleton, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { useAdminOverview } from '../hooks/useAdminOverview.js';
import { OverviewStatCard } from '../components/OverviewStatCard.js';
import { WorkerHealthCard } from '../components/WorkerHealthCard.js';
import { formatAdminCount, formatAdminSpend } from '../lib/format.js';
import type { AdminOverview } from '../api/admin.client.js';

const { Text } = Typography;

const PERIOD_OPTIONS = [7, 30, 90] as const;

function outboxTone(status: string): 'success' | 'warning' | 'error' | 'processing' | 'default' {
  switch (status) {
    case 'delivered':
    case 'sent':
      return 'success';
    case 'failed':
    case 'bounced':
    case 'spam_complaint':
      return 'error';
    case 'pending':
      return 'warning';
    case 'sending':
      return 'processing';
    default:
      return 'default';
  }
}

function OutboxSummary({ outbox }: { outbox: AdminOverview['outbox'] }): ReactElement {
  const { t } = useTranslation();

  if (outbox.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('crm.admin.outbox.empty')} />;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {outbox.map((row) => (
        <span
          key={row.status}
          data-testid={`admin-outbox-${row.status}`}
          className="flex items-center gap-1.5 rounded-lg border border-[var(--ant-color-border,#e7e5e4)] px-2.5 py-1"
        >
          <span
            aria-hidden="true"
            className="h-2 w-2 rounded-full"
            style={{
              backgroundColor:
                outboxTone(row.status) === 'success'
                  ? 'var(--ant-color-success)'
                  : outboxTone(row.status) === 'error'
                    ? 'var(--ant-color-error)'
                    : outboxTone(row.status) === 'warning'
                      ? 'var(--ant-color-warning)'
                      : 'var(--ant-color-text-quaternary)',
            }}
          />
          <Text className="text-xs text-[var(--ant-color-text-secondary)]">
            {t(`crm.admin.outbox.status_${row.status}`)}
          </Text>
          <Text className="text-xs font-semibold text-[var(--ant-color-text,#292524)]">
            {formatAdminCount(row.count)}
          </Text>
        </span>
      ))}
    </div>
  );
}

export function AdminDashboardPage(): ReactElement {
  const { t } = useTranslation();
  const [periodDays, setPeriodDays] = useState<number>(7);
  const { data, isLoading, isError, error, refetch, isFetching } = useAdminOverview(periodDays);

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        title={t('crm.admin.dashboard.load_error')}
        description={error instanceof Error ? error.message : undefined}
      />
    );
  }

  if (isLoading || data === undefined) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton active paragraph={{ rows: 2 }} />
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Skeleton active paragraph={{ rows: 1 }} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Skeleton active paragraph={{ rows: 1 }} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Skeleton active paragraph={{ rows: 1 }} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Skeleton active paragraph={{ rows: 1 }} />
            </Card>
          </Col>
        </Row>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  const workers = data.workers;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('crm.admin.dashboard.title')}
        subtitle={t('crm.admin.dashboard.subtitle')}
        actions={
          <>
            <Select
              aria-label={t('crm.admin.dashboard.period_label')}
              value={periodDays}
              onChange={setPeriodDays}
              options={PERIOD_OPTIONS.map((days) => ({
                value: days,
                label: t('crm.admin.dashboard.period_days', { days }),
              }))}
              style={{ width: 130 }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void refetch()}
              loading={isFetching}
              aria-label={t('crm.admin.dashboard.reload')}
            >
              {t('crm.admin.dashboard.reload')}
            </Button>
          </>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <OverviewStatCard
            label={t('crm.admin.dashboard.kpi_users')}
            value={formatAdminCount(data.counters.users)}
            hint={t('crm.admin.dashboard.kpi_users_24h', { count: data.counters.users24h })}
            testId="admin-kpi-users"
          />
        </Col>
        <Col xs={12} md={6}>
          <OverviewStatCard
            label={t('crm.admin.dashboard.kpi_workspaces')}
            value={formatAdminCount(data.counters.workspaces)}
            testId="admin-kpi-workspaces"
          />
        </Col>
        <Col xs={12} md={6}>
          <OverviewStatCard
            label={t('crm.admin.dashboard.kpi_deals')}
            value={formatAdminCount(data.counters.deals)}
            testId="admin-kpi-deals"
          />
        </Col>
        <Col xs={12} md={6}>
          <OverviewStatCard
            label={t('crm.admin.dashboard.kpi_messages_24h')}
            value={formatAdminCount(data.messages24h.count)}
            hint={t('crm.admin.dashboard.kpi_tokens_24h', {
              count: formatAdminCount(data.messages24h.tokens),
            })}
            testId="admin-kpi-messages"
          />
        </Col>
      </Row>

      <Card size="small" data-testid="admin-llm-spend" styles={{ body: { padding: 16 } }}>
        <Text
          className="block text-[11px] font-medium uppercase tracking-widest"
          style={{ color: 'var(--ant-color-text-secondary, #57534e)' }}
        >
          {t('crm.admin.dashboard.llm_spend_title')}
        </Text>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="font-semibold font-mono text-[20px] text-[var(--ant-color-text,#2d2418)]">
            {formatAdminSpend(data.llmSpend.costRub)}
          </span>
          <Text className="text-xs text-[var(--ant-color-text-secondary)]">
            {t('crm.admin.dashboard.llm_spend_period', {
              days: data.llmSpend.periodDays,
              messages: formatAdminCount(data.llmSpend.messages),
              tokens: formatAdminCount(data.llmSpend.tokens),
            })}
          </Text>
        </div>
      </Card>

      <div>
        <Text className="mb-2 block text-[15px] font-semibold text-[var(--ant-color-text,#44403c)]">
          {t('crm.admin.dashboard.workers_title')}
        </Text>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} xl={8}>
            <WorkerHealthCard
              title={t('crm.admin.workers.scenario_runner')}
              health={workers.scenarioRunner}
              testId="admin-worker-scenario-runner"
            />
          </Col>
          <Col xs={24} md={12} xl={8}>
            <WorkerHealthCard
              title={t('crm.admin.workers.telegram_inbound')}
              health={workers.telegramInbound}
              testId="admin-worker-telegram-inbound"
            />
          </Col>
          <Col xs={24} md={12} xl={8}>
            <WorkerHealthCard
              title={t('crm.admin.workers.telegram_delivery')}
              health={workers.telegramDelivery}
              testId="admin-worker-telegram-delivery"
            />
          </Col>
          <Col xs={24} md={12} xl={8}>
            <WorkerHealthCard
              title={t('crm.admin.workers.mail_outbox')}
              health={workers.mailOutbox}
              testId="admin-worker-mail-outbox"
            />
          </Col>
          <Col xs={24} md={12} xl={8}>
            <WorkerHealthCard
              title={t('crm.admin.workers.external_calendar_sync')}
              health={workers.externalCalendarSync}
              testId="admin-worker-calendar-sync"
            />
          </Col>
        </Row>
      </div>

      <Card size="small" styles={{ body: { padding: 16 } }} data-testid="admin-outbox-summary">
        <Text className="mb-2 block text-[15px] font-semibold text-[var(--ant-color-text,#44403c)]">
          {t('crm.admin.outbox.title')}
        </Text>
        <OutboxSummary outbox={data.outbox} />
      </Card>
    </div>
  );
}
